import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { keys } from '@/lib/query';
import { formatDate } from '@/lib/format';
import { useUiStore } from '@/stores/ui.store';
import { Button } from '@/ui/Button';
import { Checkbox } from '@/ui/Checkbox';
import { Drawer } from '@/ui/Modal';
import { EmptyState, LoadingState } from '@/ui/States';

interface Notification {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
}

export function NotificationsPanel() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const open = useUiStore((s) => s.notificationsOpen);
  const toggle = useUiStore((s) => s.toggleNotifications);

  const { data, isPending } = useQuery({
    queryKey: keys.notifications,
    queryFn: () => api.get<Notification[]>('/notifications'),
    enabled: open,
  });

  const navigate = useNavigate();

  const markRead = useMutation({
    mutationFn: (ids?: string[]) => api.post('/notifications/read', ids ? { ids } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.notifications });
      void qc.invalidateQueries({ queryKey: keys.notificationCount });
    },
  });

  return (
    <Drawer open={open} onClose={() => toggle(false)} title={t('notifications.title')}>
      {open && <EmailPreferences />}
      {isPending ? (
        <LoadingState />
      ) : !data?.length ? (
        <EmptyState icon={<BellOff className="size-5" aria-hidden />} title={t('notifications.empty')} />
      ) : (
        <div className="flex flex-col gap-2">
          <Button variant="ghost" size="sm" className="self-end"
                  loading={markRead.isPending} onClick={() => markRead.mutate(undefined)}>
            {t('notifications.markAllRead')}
          </Button>
          {data.map((n) => {
            const target = linkFor(n.payload);
            const open = () => {
              if (!target) return;
              if (!n.readAt) markRead.mutate([n.id]);
              toggle(false);
              void navigate(target);
            };
            return (
              <article key={n.id}
                       onClick={target ? open : undefined}
                       onKeyDown={target ? (e) => e.key === 'Enter' && open() : undefined}
                       role={target ? 'link' : undefined}
                       tabIndex={target ? 0 : undefined}
                       className={`rounded-[var(--radius-control)] border border-line p-3 ${n.readAt ? 'bg-surface' : 'bg-secondary-soft'} ${target ? 'cursor-pointer hover:border-secondary' : ''}`}>
                <p className="text-sm text-ink">
                  {/* Payload holds ids only; the sentence is rendered per locale
                      here. Type keys are flattened because i18next splits on ".". */}
                  {t(`notifications.types.${n.type.replace(/\./g, '_')}`, {
                    defaultValue: n.type,
                    name: String((n.payload as { requesterName?: string }).requesterName ?? ''),
                  })}
                </p>
                <p className="mt-1 text-[11px] text-ink-subtle">
                  {formatDate(n.createdAt, i18n.language)}
                </p>
              </article>
            );
          })}
        </div>
      )}
    </Drawer>
  );
}

const TASK_EMAILS = ['item_assigned', 'item_updated', 'item_mentioned'] as const;
type TaskEmail = (typeof TASK_EMAILS)[number];

/** Which task emails this person gets. The bell shows everything either way. */
function EmailPreferences() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: keys.emailPreferences,
    queryFn: () => api.get<Record<TaskEmail, boolean>>('/notifications/email-preferences'),
  });
  const save = useMutation({
    mutationFn: (patch: Partial<Record<TaskEmail, boolean>>) =>
      api.put<Record<TaskEmail, boolean>>('/notifications/email-preferences', patch),
    onSuccess: (next) => qc.setQueryData(keys.emailPreferences, next),
  });

  return (
    <fieldset className="mb-4 flex flex-col gap-2 border-b border-line pb-4">
      <legend className="mb-2 text-xs font-semibold text-ink-muted uppercase">
        {t('notifications.email.title')}
      </legend>
      {TASK_EMAILS.map((kind) => (
        <Checkbox key={kind} label={t(`notifications.email.${kind}`)}
                  checked={data?.[kind] ?? true} disabled={!data || save.isPending}
                  onChange={(e) => save.mutate({ [kind]: e.target.checked })} />
      ))}
    </fieldset>
  );
}

/**
 * Task notifications carry the board and item, doc ones the doc and, for a
 * comment, its thread - enough to open what they are about.
 */
function linkFor(payload: Record<string, unknown>) {
  const { boardId, itemId, docId, commentId } = payload as {
    boardId?: string; itemId?: string; docId?: string; commentId?: string;
  };
  if (docId) return `/docs/${docId}${commentId ? `?comment=${commentId}` : ''}`;
  return boardId && itemId ? `/boards/${boardId}?item=${itemId}` : null;
}
